import { useMemo } from "react";
import dagre from "dagre";
import { Database, Table2, Columns3, Cloud, FileText, Box, Snowflake } from "lucide-react";
import "reactflow/dist/style.css";
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  BackgroundVariant,
  Position,
  Handle,
  MarkerType,
  NodeProps,
} from "reactflow";
import { cn } from "~/lib/utils";
import {
  LineageNode,
  LineageEdge,
  TableLineageResponse,
  NodeDirection,
  EnhancedNodeData,
  ServiceType,
  isTableNode,
  isColumnNode,
  isContainerNode,
} from "~/pages/datasets/types/interfaces/lineage";

interface TableLineageGraphProps {
  data: TableLineageResponse;
  currentTableFQN: string;
}

/**
 * Service configuration for rendering nodes.
 */
interface ServiceConfig {
  buildBreadcrumb: (node: LineageNode) => string;
  getDisplayInfo: (node: LineageNode) => {
    icon: React.ComponentType<{ className?: string }>;
    serviceIcon: React.ComponentType<{ className?: string }>;
    entityType: string;
    color: string;
    serviceColor: string;
  };
}

const SERVICE_REGISTRY = {
  [ServiceType.SNOWFLAKE]: {
    buildBreadcrumb: (node) => {
      if (isTableNode(node)) {
        return `${node.service || ServiceType.SNOWFLAKE} / ${node.databaseName} / ${node.schemaName} / ${node.name}`;
      }
      if (isColumnNode(node)) {
        return `${node.service || ServiceType.SNOWFLAKE} / ${node.databaseName} / ${node.schemaName} / ${node.table}`;
      }
      return node.fullyQualifiedName || node.name;
    },
    getDisplayInfo: (node) => {
      if (isColumnNode(node)) {
        return {
          icon: Columns3,
          serviceIcon: Snowflake,
          entityType: "Column",
          color: "text-purple-600 dark:text-purple-400",
          serviceColor: "text-blue-400",
        };
      }
      return {
        icon: Table2,
        serviceIcon: Snowflake,
        entityType: "Table",
        color: "text-blue-600 dark:text-blue-400",
        serviceColor: "text-blue-400",
      };
    },
  },
  [ServiceType.S3]: {
    buildBreadcrumb: (node) => {
      // Use pre-parsed fields from API
      if (isContainerNode(node)) {
        const filePath = node.filePath || "";
        if (filePath) {
          const pathParts = filePath.split("/");
          const filename = pathParts[pathParts.length - 1]?.replace(/"/g, "") || "";
          const path = pathParts.slice(0, -1).join(" / ");
          return path
            ? `${node.service || ServiceType.S3} / ${node.bucket} / ${path} / ${filename}`
            : `${node.service || ServiceType.S3} / ${node.bucket} / ${filename}`;
        }
        return `${node.service || ServiceType.S3} / ${node.bucket} / ${node.name}`;
      }
      return node.fullyQualifiedName || node.name;
    },
    getDisplayInfo: () => ({
      icon: FileText,
      serviceIcon: Cloud,
      entityType: "File",
      color: "text-orange-600 dark:text-orange-400",
      serviceColor: "text-orange-400",
    }),
  },
} satisfies Record<string, ServiceConfig>;

/**
 * Generic fallback configuration for unknown service types.
 */
const FALLBACK_CONFIG: ServiceConfig = {
  buildBreadcrumb: (node) => node.fullyQualifiedName || node.name,
  getDisplayInfo: () => ({
    icon: Box,
    serviceIcon: Database,
    entityType: "Entity",
    color: "text-muted-foreground",
    serviceColor: "text-muted-foreground",
  }),
};

/**
 * Get icon, color, and display info for a node using service-specific configuration.
 */
const getNodeDisplayInfo = (data: LineageNode) => {
  const config = data.service
    ? (SERVICE_REGISTRY as Record<string, ServiceConfig>)[data.service] || FALLBACK_CONFIG
    : FALLBACK_CONFIG;

  return {
    ...config.getDisplayInfo(data),
    breadcrumb: config.buildBreadcrumb(data),
  };
};

/**
 * Custom node component with OpenMetadata-inspired styling.
 */
const TableNode = ({ data, selected }: NodeProps<EnhancedNodeData>) => {
  const displayInfo = getNodeDisplayInfo(data);
  const Icon = displayInfo.icon;
  const ServiceIcon = displayInfo.serviceIcon;

  const borderColor =
    data.direction === NodeDirection.CURRENT
      ? "border-primary"
      : data.direction === NodeDirection.UPSTREAM
        ? "border-blue-500"
        : "border-green-500";

  const bgColor =
    data.direction === NodeDirection.CURRENT
      ? "bg-primary/5"
      : data.direction === NodeDirection.UPSTREAM
        ? "bg-blue-50 dark:bg-blue-950/50"
        : "bg-green-50 dark:bg-green-950/50";

  return (
    <div
      data-testid={`lineage-node-${data.direction}`}
      data-node-id={data.id}
      data-node-name={data.name}
      className={cn(
        "relative min-w-[280px] max-w-[400px] rounded-lg bg-card border-2 transition-all group",
        borderColor,
        data.isCurrentTable && "shadow-lg",
        !data.isCurrentTable && "shadow-sm hover:shadow-md",
        selected && "ring-2 ring-primary ring-offset-2"
      )}
      title={data.fullyQualifiedName || data.name}
    >
      <Handle
        type="target"
        position={Position.Left}
        id="target"
        className="!-left-2 !h-3 !w-3 !bg-card !border-2 !border-border !z-10"
      />

      <div className="space-y-2 relative z-0">
        {/* Header with full breadcrumb */}
        <div className={cn("px-3 py-2 border-b", bgColor)}>
          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-mono truncate">
            <ServiceIcon className={cn("h-3 w-3 flex-shrink-0", displayInfo.serviceColor)} />
            <span className="truncate">{displayInfo.breadcrumb}</span>
          </div>
        </div>

        {/* Main content with icon and name */}
        <div className="px-3 pb-3 pt-1">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 rounded-md bg-muted flex-shrink-0">
              <Icon className={cn("h-5 w-5", displayInfo.color)} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate text-foreground">{data.name}</div>
              <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
                {displayInfo.entityType}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Right}
        id="source"
        className="!-right-2 !h-3 !w-3 !bg-card !border-2 !border-border !z-10"
      />
    </div>
  );
};

const nodeTypes = {
  tableNode: TableNode,
};

/**
 * Helper function to traverse graph from a starting node via BFS.
 */
function traverseFrom(
  startId: string,
  adjacencyMap: Map<string, Set<string>>,
  direction: NodeDirection,
  directions: Map<string, NodeDirection>
) {
  const queue = [startId];
  const visited = new Set([startId]);

  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    const neighbors = adjacencyMap.get(nodeId) || new Set();

    neighbors.forEach((neighborId) => {
      if (!visited.has(neighborId)) {
        visited.add(neighborId);
        directions.set(neighborId, direction);
        queue.push(neighborId);
      }
    });
  }
}

/**
 * Classify nodes as upstream/downstream/current via BFS.
 */
function classifyNodeDirections(
  nodes: LineageNode[],
  edges: LineageEdge[],
  currentTableFQN: string
): Map<string, NodeDirection> {
  const directions = new Map<string, NodeDirection>();
  const currentNode = nodes.find((n) => n.fullyQualifiedName === currentTableFQN);

  if (!currentNode) {
    nodes.forEach((n) => directions.set(n.id, NodeDirection.UPSTREAM));
    return directions;
  }

  directions.set(currentNode.id, NodeDirection.CURRENT);

  const upstreamMap = new Map<string, Set<string>>();
  const downstreamMap = new Map<string, Set<string>>();

  edges.forEach((edge) => {
    if (!downstreamMap.has(edge.source)) {
      downstreamMap.set(edge.source, new Set());
    }
    if (!upstreamMap.has(edge.target)) {
      upstreamMap.set(edge.target, new Set());
    }
    downstreamMap.get(edge.source)!.add(edge.target);
    upstreamMap.get(edge.target)!.add(edge.source);
  });

  traverseFrom(currentNode.id, upstreamMap, NodeDirection.UPSTREAM, directions);
  traverseFrom(currentNode.id, downstreamMap, NodeDirection.DOWNSTREAM, directions);

  return directions;
}

// Layout constants - tuned for optimal readability with 280px wide nodes
const GRAPH_LAYOUT = {
  rankdir: "LR" as const, // Left-to-right flow (upstream → current → downstream)
  ranksep: 240, // Horizontal spacing between ranks (columns)
  nodesep: 100, // Vertical spacing between nodes in the same rank
} as const;

const NODE_DIMENSIONS = {
  width: 280, // Must match min-w-[280px] in TableNode component
  height: 100, // Approximate height for layout calculation
} as const;

/**
 * Apply Dagre layout with left-to-right flow.
 */
const applyDagreLayout = (
  nodes: LineageNode[],
  edges: LineageEdge[],
  currentTableFQN: string
): { nodes: Node<EnhancedNodeData>[]; edges: Edge[] } => {
  const directions = classifyNodeDirections(nodes, edges, currentTableFQN);

  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph(GRAPH_LAYOUT);

  const { width: nodeWidth, height: nodeHeight } = NODE_DIMENSIONS;

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutNodes: Node<EnhancedNodeData>[] = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    const direction = directions.get(node.id) || NodeDirection.UPSTREAM;

    return {
      id: node.id,
      type: "tableNode",
      position: {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      },
      data: {
        ...node,
        direction,
        isCurrentTable: direction === NodeDirection.CURRENT,
      },
    };
  });

  const layoutEdges: Edge[] = edges.map((edge) => {
    const sourceDirection = directions.get(edge.source) || NodeDirection.UPSTREAM;
    const strokeColor =
      sourceDirection === NodeDirection.CURRENT
        ? "rgb(99, 102, 241)"
        : sourceDirection === NodeDirection.UPSTREAM
          ? "rgb(37, 99, 235)"
          : "rgb(22, 163, 74)";

    return {
      id: edge.id,
      source: edge.source,
      target: edge.target,
      label: edge.label,
      type: "default",
      animated: false,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: strokeColor,
        width: 20,
        height: 20,
      },
      style: {
        stroke: strokeColor,
        strokeWidth: sourceDirection === NodeDirection.CURRENT ? 2.5 : 2,
      },
    };
  });

  return { nodes: layoutNodes, edges: layoutEdges };
};

export const TableLineageGraph = ({ data, currentTableFQN }: TableLineageGraphProps) => {
  const { nodes, edges } = useMemo(
    () => applyDagreLayout(data.nodes, data.edges, currentTableFQN),
    [data.nodes, data.edges, currentTableFQN]
  );

  if (data.nodes.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <Database className="mx-auto h-12 w-12 text-muted-foreground" />
          <p className="mt-4 text-sm text-muted-foreground">No lineage data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
        minZoom={0.1}
        maxZoom={2}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={true}
        defaultEdgeOptions={{
          type: "default",
          animated: false,
        }}
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
};

/**
 * Testing utilities - exported for test usage only.
 * @internal
 */
export const __testing__ = {
  classifyNodeDirections,
  traverseFrom,
};
